import React from 'react';
import PropTypes from 'prop-types';
import {TextInput, Dropdown} from '@/components/atoms';
import styles from '../../../RewardSetup.module.scss';
import {preventInvalidNumericInput} from '@/utils/inputValidations';
import {useAppSelector} from '@/store';
import {getCurrencySymbol} from '@/utils/commons';

/**
 * BonusProductsCondition component displays the bonus products reward condition UI
 * Handles product selection via modal triggered by dropdown option selection, order quantity, and bonus product limit inputs
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing redeemPoints, rewardCashAmount, cardType
 * @param {Object} props.validation - Validation state object
 * @param {Array} props.rewardCardTypes - Array of reward card type options
 * @param {boolean} props.rewardCardTypesLoading - Loading state for reward card types
 * @param {Function} props.handleRedeemPointsChange - Handler for redeem points change
 * @param {Function} props.handleCardTypeChange - Handler for card type change
 * @param {Function} props.handleRewardCashAmountChange - Handler for reward cash amount change
 * @param {string} props.redeemPointsError - Error message for redeem points
 * @param {string} props.rewardCashAmountError - Error message for reward cash amount
 */
const VisaCard = ({
  formData,
  validation,
  rewardCardTypes,
  rewardCardTypesLoading,
  handleRedeemPointsChange,
  handleCardTypeChange,
  handleRewardCashAmountChange,
  redeemPointsError,
  rewardCashAmountError
}) => {
  const selectedOptions = formData.cardType
    ? rewardCardTypes.filter((rt) => rt.value === formData.cardType)
    : [];
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const currencySymbol = getCurrencySymbol(countryCode);

  return (
    <div className='flex flex-wrap gap-[13px]'>
      <div
        className={`${styles.rewardSetup__cardTypeDropdownWrapper} text-left`}>
        <Dropdown
          className='text-sm placeholder:text-sm'
          value={selectedOptions} // Show selected products
          isError={validation.cardTypeValid}
          errorMessage={
            validation.cardTypeValid ? 'At least one type must be selected' : ''
          }
          isDark={true}
          childList={rewardCardTypes}
          placeholder={rewardCardTypesLoading ? 'Loading...' : 'Card Type'}
          isJsonWrap={false}
          selection='single'
          hideMenuListOnSelection={true}
          isClearable={false}
          updateValue={handleCardTypeChange}
          style={{maxWidth: '180px'}}
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>{currencySymbol}</span>
      <div className={`${styles.rewardSetup__visaCardInputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.rewardCashAmount}
          onChange={handleRewardCashAmountChange}
          label=''
          placeholderText='Value'
          error={rewardCashAmountError}
          isDark={true}
          type='number'
          handleKeyDown={preventInvalidNumericInput}
          id='reward-cash-amount'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>by redeeeming</span>
      <div className={`${styles.rewardSetup__visaCardInputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.redeemPoints}
          onChange={handleRedeemPointsChange}
          label=''
          placeholderText='Points'
          error={redeemPointsError}
          isDark={true}
          handleKeyDown={preventInvalidNumericInput}
          type='number'
          id='redeem-points'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>points.</span>
    </div>
  );
};

VisaCard.propTypes = {
  formData: PropTypes.shape({
    redeemPoints: PropTypes.string,
    rewardCashAmount: PropTypes.string,
    cardType: PropTypes.string
  }).isRequired,
  validation: PropTypes.shape({
    cardTypeValid: PropTypes.bool
  }).isRequired,
  rewardCardTypes: PropTypes.array.isRequired,
  rewardCardTypesLoading: PropTypes.bool.isRequired,
  handleRedeemPointsChange: PropTypes.func.isRequired,
  handleCardTypeChange: PropTypes.func.isRequired,
  handleRewardCashAmountChange: PropTypes.func.isRequired,
  redeemPointsError: PropTypes.string,
  rewardCashAmountError: PropTypes.string
};

export default VisaCard;
